import { create } from 'zustand'
import type { DecalData, AvailableDecal, DecalConfiguration } from '../types/decal'

interface DecalState {
  // Decal data
  decals: DecalData[]
  availableDecals: AvailableDecal[]

  // UI state
  isPlacementMode: boolean
  selectedDecalUrl: string | null
  selectedDecalId: string | null
  placementScale: { x: number; y: number; z: number }

  // Actions - Decal management
  addDecal: (decalData: Omit<DecalData, 'id'>) => string
  removeDecal: (id: string) => void
  updateDecal: (id: string, updates: Partial<DecalData>) => void
  clearAllDecals: () => void
  getDecalById: (id: string) => DecalData | undefined

  // Actions - Available decals
  loadAvailableDecals: () => Promise<void>
  setSelectedDecalUrl: (url: string | null) => void

  // Actions - UI
  togglePlacementMode: () => void
  setPlacementMode: (active: boolean) => void
  setSelectedDecalId: (id: string | null) => void
  setPlacementScale: (scale: { x: number; y: number; z: number }) => void

  // Actions - Import/Export
  exportConfiguration: () => DecalConfiguration
  importConfiguration: (config: DecalConfiguration) => void
  loadDefaultConfiguration: () => Promise<void>

  // Persistence
  isDirty: boolean
  isSaving: boolean
  saveToServer: () => Promise<void>
  loadFromServer: () => Promise<void>
}

/**
 * Generate a unique ID for decals
 */
const generateDecalId = (): string => {
  return `decal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Format filename to display name
 * Example: "warning-sign.png" -> "Warning Sign"
 */
const formatDecalName = (filename: string): string => {
  const nameWithoutExt = filename.replace(/\.(png|jpg|jpeg|webp|svg)$/i, '')
  return nameWithoutExt
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Scan the /public/decals/ folder for available textures
 * Since we can't directly read filesystem in browser, we'll use a common pattern:
 * Try to fetch known image extensions and build a list
 *
 * For production use, you would either:
 * 1. Generate a manifest file during build
 * 2. Use a server endpoint to list files
 * 3. Manually maintain a list
 *
 * For now, we'll implement a hybrid approach:
 * - Try to load default-decals.json if it exists
 * - Otherwise provide an empty array (user can add decals at runtime)
 */
const scanDecalsFolder = async (): Promise<AvailableDecal[]> => {
  const availableDecals: AvailableDecal[] = []

  try {
    // Try to fetch a decals manifest if it exists
    const response = await fetch('/decals-manifest.json')
    if (response.ok) {
      const manifest = await response.json()
      return manifest.decals || []
    }
  } catch (error) {
    console.log('No decals-manifest.json found, will use empty list')
  }

  // Alternative: Check common decal filenames
  // This is a fallback for development
  const commonNames = [
    'warning',
    'danger',
    'caution',
    'arrow',
    'number-1',
    'number-2',
    'logo',
    'sign',
    'symbol',
    'marking',
    'stripe',
    'text',
  ]

  const extensions = ['png', 'jpg', 'jpeg', 'webp']

  for (const name of commonNames) {
    for (const ext of extensions) {
      const url = `/decals/${name}.${ext}`
      try {
        const response = await fetch(url, { method: 'HEAD' })
        if (response.ok) {
          availableDecals.push({
            id: name,
            name: formatDecalName(`${name}.${ext}`),
            url,
          })
          break // Found this name, move to next
        }
      } catch (error) {
        // File doesn't exist, continue
      }
    }
  }

  return availableDecals
}

export const useDecalStore = create<DecalState>((set, get) => ({
  // Initial state
  decals: [],
  availableDecals: [],
  isPlacementMode: false,
  selectedDecalUrl: null,
  selectedDecalId: null,
  placementScale: { x: 1, y: 1, z: 1 },
  isDirty: false,
  isSaving: false,

  // Decal management
  addDecal: (decalData) => {
    const id = generateDecalId()
    const newDecal: DecalData = { id, ...decalData }

    set((state) => ({
      decals: [...state.decals, newDecal],
      isDirty: true,
    }))

    console.log(`[DecalStore] Added decal: ${id} on mesh: ${decalData.targetMeshName}`)
    return id
  },

  removeDecal: (id) => {
    set((state) => ({
      decals: state.decals.filter((d) => d.id !== id),
      selectedDecalId: state.selectedDecalId === id ? null : state.selectedDecalId,
      isDirty: true,
    }))
    console.log(`[DecalStore] Removed decal: ${id}`)
  },

  updateDecal: (id, updates) => {
    set((state) => ({
      decals: state.decals.map((decal) => (decal.id === id ? { ...decal, ...updates } : decal)),
      isDirty: true,
    }))
  },

  clearAllDecals: () => {
    set({
      decals: [],
      selectedDecalId: null,
      isDirty: true,
    })
    console.log('[DecalStore] Cleared all decals')
  },

  getDecalById: (id) => {
    return get().decals.find((d) => d.id === id)
  },

  // Available decals
  loadAvailableDecals: async () => {
    try {
      const decals = await scanDecalsFolder()
      set({ availableDecals: decals })
      console.log(`[DecalStore] Loaded ${decals.length} available decals`)
    } catch (error) {
      console.error('[DecalStore] Error loading available decals:', error)
      set({ availableDecals: [] })
    }
  },

  setSelectedDecalUrl: (url) => {
    set({ selectedDecalUrl: url })
  },

  // UI actions
  togglePlacementMode: () => {
    set((state) => ({
      isPlacementMode: !state.isPlacementMode,
    }))
  },

  setPlacementMode: (active) => {
    set({ isPlacementMode: active })
  },

  setSelectedDecalId: (id) => {
    set({ selectedDecalId: id })
  },

  setPlacementScale: (scale) => {
    set({ placementScale: scale })
  },

  // Import/Export
  exportConfiguration: () => {
    const state = get()
    const config: DecalConfiguration = {
      version: '1.0',
      decals: state.decals,
      metadata: {
        exportDate: new Date().toISOString(),
      },
    }
    return config
  },

  importConfiguration: (config) => {
    if (config.version !== '1.0') {
      console.warn('[DecalStore] Unknown configuration version:', config.version)
    }

    set({
      decals: config.decals || [],
      selectedDecalId: null,
      isDirty: true,
    })

    console.log(`[DecalStore] Imported ${config.decals?.length || 0} decals`)
  },

  loadDefaultConfiguration: async () => {
    try {
      const response = await fetch('/default-decals.json')
      if (!response.ok) {
        console.log('[DecalStore] No default-decals.json found')
        return
      }

      const config = await response.json()
      get().importConfiguration(config)
      set({ isDirty: false })
      console.log('[DecalStore] Loaded default decal configuration')
    } catch (error) {
      console.error('[DecalStore] Error loading default configuration:', error)
    }
  },

  saveToServer: async () => {
    set({ isSaving: true })
    console.log('[DecalStore] Save disabled in static mode')
    set({ isDirty: false, isSaving: false })
  },

  loadFromServer: async () => {
    try {
      const res = await fetch('/data/decal-config.json')
      if (res.ok) {
        const config = await res.json()
        if (config && config.decals) {
          get().importConfiguration(config)
          set({ isDirty: false })
        }
      }
    } catch {
      console.log('[DecalStore] Config not available, using defaults')
    }
  },
}))

export default useDecalStore
